import React from "react";
import { Grid, Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { ConfigSection } from "../ConfigSection";
import { StyledTextField } from "../StyledTextField";
import {
  validateFuzziness,
  validatePrefixLength,
  validateMaxExpansions,
} from "../../utils/validation";
import { COLORS } from "../../constants";
import { FuzzyConfigSectionProps } from "../../types";

export const FuzzyConfigSection: React.FC<FuzzyConfigSectionProps> = ({
  config,
  updateFuzzyConfig,
}) => (
  <ConfigSection
    title="Fuzzy Search Configuration"
    tooltip="This controls how forgiving the search is when you make typos or spelling mistakes. For example, if you search for 'aple' but meant 'apple', fuzzy search can still find the right results. You can adjust how many spelling errors it tolerates and how strict it should be about finding matches."
  >
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Fuzziness"
          value={config.fuzzyConfig?.fuzziness ?? ""}
          validate={validateFuzziness}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateFuzzyConfig({ fuzziness: value || undefined });
          }}
          helperText="Number of letter changes allowed (add, remove, swap). Use AUTO or 1–2 edits for typos."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Prefix Length"
          type="number"
          inputProps={{ min: 0, max: 100 }}
          value={config.fuzzyConfig?.prefixLength ?? ""}
          validate={validatePrefixLength}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateFuzzyConfig({
              prefixLength: value === "" ? undefined : parseInt(value),
            });
          }}
          helperText="How many starting letters must match exactly before fuzziness applies. Range: 0–100 (commonly 1–3)."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Max Expansions"
          type="number"
          inputProps={{ min: 1, max: 1000 }}
          value={config.fuzzyConfig?.maxExpansions ?? ""}
          validate={validateMaxExpansions}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateFuzzyConfig({
              maxExpansions: value === "" ? undefined : parseInt(value),
            });
          }}
          helperText="Maximum variations to check for fuzzy matches. Range: 1–1,000. Higher values improve recall but slow performance."
        />
      </Grid>
      <Grid item xs={6}>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={config.fuzzyConfig?.transpositions ?? false}
                onChange={(e) =>
                  updateFuzzyConfig({
                    transpositions: e.target.checked,
                  })
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: COLORS.PRIMARY,
                    "&:hover": {
                      backgroundColor: COLORS.PRIMARY_ALPHA,
                    },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: COLORS.PRIMARY,
                  },
                }}
              />
            }
            label="Enable Transpositions"
          />
          <Typography variant="caption" color="textSecondary" display="block">
            Allows swapping two letters (e.g., “ipone” → “iPhone”).
          </Typography>
        </Box>
      </Grid>
    </Grid>
  </ConfigSection>
);
